import React, { type ButtonHTMLAttributes } from 'react';
import Spinner from './Spinner';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'solid' | 'outline' | 'plain';
  color?: 'primary' | 'dark';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  icon?: boolean;
  isLoading?: boolean;
};

const Button = (props: ButtonProps) => {
  const {
    children,
    variant = 'solid',
    color = 'primary',
    size = 'lg',
    className = '',
    icon = false,
    disabled = false,
    isLoading = false,
    ...rest
  } = props;

  const variantClasses = {
    solid: {
      primary:
        'bg-primary-400 text-white border-2 border-primary-400 hover:bg-primary-600 hover:border-primary-600 focus:ring-2 focus:ring-purple-50 active:bg-primary-600 hover-shadow-primary-light',
      dark: 'bg-heading-500 text-white border-2 border-heading-500 hover:bg-white hover:text-heading-500 focus:ring-2 focus:ring-purple-50 active:bg-purple-25',
    },
    outline: {
      primary:
        'bg-transparent text-gray-900 border-2 border-gray-900 hover:bg-purple-25 hover:text-primary-300 hover:border-primary-300 focus:ring-2 focus:ring-purple-50 active:bg-purple-25',
      dark: 'bg-transparent text-gray-900 border-2 border-gray-900 hover:bg-purple-25 hover:text-primary-300 hover:border-primary-300 focus:ring-2 focus:ring-purple-50 active:bg-purple-25',
    },
    plain: {
      primary:
        'bg-transparent text-primary-400 hover:bg-primary-500 focus:ring-2 focus:ring-purple-50 active:bg-purple-25',
      dark: 'bg-transparent text-primary-400 hover:bg-primary-50 focus:ring-2 focus:ring-purple-50 active:bg-purple-25',
    },
  };

  const disabledVariantClasses = {
    solid: 'bg-gray-200 text-gray-400 border-2 border-gray-200',
    outline: 'bg-transparent text-gray-400 border-2 border-gray-200',
    plain: 'bg-transparent text-gray-400',
  };

  const sizeClasses = {
    xs: 'px-2 py-[0.375rem] h-7 min-w-7 text-[0.625rem] leading-[0.75rem] font-semibold rounded-sm',
    sm: 'px-3 py-2 h-8 min-w-8 text-[0.75rem] leading-[1rem] font-medium rounded-md',
    md: 'px-4 py-3 h-10 min-w-10 text-[0.875rem] leading-[1.25rem] font-regular rounded-lg',
    lg: 'px-5 py-[0.875rem] h-12 min-w-12 text-[1rem] leading-[1.25rem] font-regular rounded-xl',
    xl: 'px-6 py-4 h-14 min-w-14 text-[1.125rem] leading-[1.5rem] font-semibold rounded-xl',
  };

  const iconClass = {
    xs: '!p-0 w-7 [&>svg]:w-4 [&>svg]:h-4',
    sm: '!p-0 w-8 [&>svg]:w-6 [&>svg]:h-6',
    md: '!p-0 w-10 [&>svg]:w-6 [&>svg]:h-6',
    lg: '!p-0 w-12 [&>svg]:w-6 [&>svg]:h-6',
    xl: '!p-0 w-14 [&>svg]:w-6 [&>svg]:h-6',
  };

  const disabledClasses = '!cursor-not-allowed';
  const baseClassName = `px-4 py-2 cursor-pointer duration-300 flex items-center justify-center gap-2 font-medium disabled:opacity-50 curveRadius focus-visible:!outline-none ${isLoading ? 'cursor-not-allowed pointer-events-none' : ''}`;
  const computedClassName = `${baseClassName} ${sizeClasses[size]} ${className} ${disabled ? disabledVariantClasses[variant] + ' ' + disabledClasses : variantClasses[variant][color]} ${icon ? iconClass[size] : ''}`;

  return (
    <button {...rest} className={computedClassName}>
      {isLoading ? (
        <Spinner size='sm' color={variant === 'solid' ? 'white' : 'primary'} />
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
